<template>
    <!-- The kiosk's language switch: one button per language the facility
         offers, all of them on screen at once. A tablet in a lobby has the
         room for that, and a guest who cannot read the current language
         cannot be asked to open a dropdown labelled in it first. -->
    <div class="lang-bar">
        <button
            v-for="lang in languages"
            :key="lang.code"
            type="button"
            class="lang-pill"
            :class="{ active: current === lang.code }"
            @click="select(lang.code)"
        >
            {{ nativeName(lang.code) }}
        </button>
    </div>
</template>

<script>
import { nativeLanguageName, rememberKioskLanguage } from './kiosk_language';

export default {
    name: 'KioskLanguageBar',
    props: {
        // The facility's languages, already ordered (see loadKioskLanguages).
        languages: { type: Array, default: () => [] },
    },
    computed: {
        // Which button is lit. Read straight off i18n rather than copied into
        // data, so the button row and the text under it can never disagree
        // about what the kiosk is currently speaking.
        current() {
            return this.$i18n.locale;
        },
    },
    watch: {
        // A facility that does not offer the language the kiosk opened in
        // cannot stay in it: fall back to the first one it does offer, so the
        // lit button is always one the guest can actually see on screen.
        languages: {
            immediate: true,
            handler(languages) {
                if (!languages.length) return;
                if (languages.some((lang) => lang.code === this.current)) return;

                this.select(languages[0].code);
            },
        },
    },
    methods: {
        nativeName(code) {
            return nativeLanguageName(code);
        },

        select(code) {
            this.$i18n.locale = code;
            // Carried into the screens after this one: every kiosk navigation
            // is a full page load, which would otherwise open back in Japanese
            // (see kiosk_language.js).
            rememberKioskLanguage(code);
            // The host reloads whatever it holds in the guest's language (the
            // account's custom messages, the form's questions).
            this.$emit('select-lang', code);
        },
    },
};
</script>

<style lang="scss" scoped>
/* Sized off the mockup at iPad width (820px): 200px buttons under a rule that
   spans the screen. They share the row equally so a facility with five
   languages still fits on one line; more than that wraps. */
.lang-bar {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 16px clamp(16px, 3vw, 40px);
    padding: 0 0 20px;
    margin-bottom: clamp(24px, 6vh, 64px);
    border-bottom: 1px solid #d9d9d9;
    font-family: 'Poppins', sans-serif;

    .lang-pill {
        flex: 1 1 0;
        min-width: 100px;
        max-width: 200px;
        min-height: 52px;
        padding: 0.375rem 0.5rem;
        font-size: clamp(16px, 2.8vw, 24px);
        font-weight: 600;
        background: #fff;
        border: 1px solid #333;
        color: #333;

        &.active {
            background: #9e9e9e;
            border-color: #9e9e9e;
            color: #fff;
        }
    }
}
</style>
